import React, { useCallback, useEffect, useRef, useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useSupportsHover } from 'hooks/useSupportsHover';

import { useDragScroll } from './useDragScroll';

import styles from './HorizontalScrollRow.module.scss';

interface HorizontalScrollRowProps {
  className?: string;
  children: React.ReactNode;
}

type Side = 'left' | 'right';

interface ScrollArrowProps {
  side: Side;
  /** Painted AND clickable — one flag for both, so they cannot disagree. */
  live: boolean;
  /** This direction still has travel. */
  canScroll: boolean;
  /** Out of the accessibility tree: the row cannot scroll at all and this arrow does not hold focus. */
  hiddenFromAT: boolean;
  onPointerDown: () => void;
  onFocus: () => void;
  onBlur: () => void;
  onClick: () => void;
}

// One arrow, either side (review, 2026-09-16: the pair was ~40 lines
// duplicated with left/right swapped). Module-level on purpose — a component
// type created inside the row's render is a NEW type on every render, so React
// would remount the button and drop the very focus the `live` exemption below
// tracks.
const ScrollArrow: React.FC<ScrollArrowProps> = ({
  side,
  live,
  canScroll,
  hiddenFromAT,
  onPointerDown,
  onFocus,
  onBlur,
  onClick
}) => (
  <button
    type='button'
    aria-label={side === 'left' ? 'Scroll left' : 'Scroll right'}
    aria-hidden={hiddenFromAT}
    aria-disabled={!canScroll}
    tabIndex={canScroll ? 0 : -1}
    // The visual result — opacity AND pointer-events — is the stylesheet's
    // (`.arrow[data-live]`, HorizontalScrollRow.module.scss). The invariant
    // that a painted chevron must also take the click (JRam, 2026-09-14) is
    // one attribute here and one rule there, not two expressions to keep in
    // step by hand.
    data-live={live}
    className={`${styles.arrow} ${side === 'left' ? styles.arrowLeft : styles.arrowRight}`}
    onPointerDown={onPointerDown}
    onFocus={onFocus}
    onBlur={onBlur}
    onClick={onClick}>
    {side === 'left' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
  </button>
);

// Horizontal scroll row: drag-to-scroll with momentum on mouse, plus hover
// arrows on desktop that scroll by ~one viewport per click. Touch devices
// skip the hover handlers (iOS Safari emulates mouseenter on first tap and
// would otherwise suppress the child card's click).
//
// Drag listeners attach to the wrapper (not the inner scroll div) so pointer
// events that land on the overlay arrow buttons still bubble up and trigger
// drag-scroll — without this the leftmost / rightmost ~40px of the row felt
// "dead" because the buttons were absorbing pointerdown.
const HorizontalScrollRow: React.FC<HorizontalScrollRowProps> = ({ className, children }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  useDragScroll(scrollRef, { eventTargetRef: wrapperRef });

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  // Whether the row overflows AT ALL, which is a different question from
  // whether it can still scroll in a given direction — see the arrows below.
  const [canScrollAtAll, setCanScrollAtAll] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  // EN-10516: the arrows used to be focusable only while the mouse was over the
  // row, so a keyboard-only user could never reach them — the row's scroll
  // controls simply did not exist without a pointer. Focus now reveals them the
  // same way hover does, and they stay tabbable whenever the row can scroll.
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  // WHICH arrow holds focus, not merely whether the row does — see the arrows
  // below, where it exempts exactly one of them from being hidden or dimmed.
  const [focusedArrow, setFocusedArrow] = useState<Side | null>(null);
  // Chromium focuses a button on mouse click, so "focused" alone is not
  // "keyboard user". Without this gate a mouse click that spends a direction
  // left the arrow painted at full opacity with pointer-events off, and the
  // next click on that phantom chevron fell through to the tile beneath it.
  // pointerdown fires before focus, so a pointer-initiated focus is known by
  // the time onFocus runs. (`:focus-visible` is the right primitive, but this
  // jsdom rejects it as a selector.)
  const pointerFocusPending = useRef(false);
  const focusArrow = (side: Side) => {
    if (!pointerFocusPending.current) setFocusedArrow(side);
    pointerFocusPending.current = false;
  };
  const supportsHover = useSupportsHover();
  const arrowsRevealed = isHovered || isFocusWithin;

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    setCanScrollAtAll(el.scrollWidth > el.clientWidth);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll, { passive: true });

    // The box AND its content. A ResizeObserver on an `overflow-x` box never
    // fires for its CHILDREN — the box itself is unchanged — so tiles arriving
    // or leaving on a lobby tick left these flags stale while `scrollLeft`
    // stayed put and no scroll event fired. They drive `aria-hidden`, so stale
    // means arrows that lie about themselves.
    //
    // Observing the content rather than putting `children` in the deps: every
    // call site passes a freshly-mapped array, so a dep would tear down and
    // rebuild both observers — and force a synchronous layout read — once per
    // row per render, on a lobby that re-renders on every socket tick
    // (EN-9866). This pays once per actual content change (round 4).
    const resize = new ResizeObserver(checkScroll);
    const syncObserved = () => {
      resize.disconnect();
      resize.observe(el);
      Array.from(el.children).forEach((child) => resize.observe(child));
      checkScroll();
    };
    syncObserved();
    const mutations = new MutationObserver(syncObserved);
    mutations.observe(el, { childList: true });

    return () => {
      el.removeEventListener('scroll', checkScroll);
      resize.disconnect();
      mutations.disconnect();
    };
  }, [checkScroll]);

  const scroll = (direction: Side) => {
    const el = scrollRef.current;
    if (!el) return;
    // Page-style: jump by ~90% of the visible row width. Lands at a
    // logical "next screenful" boundary rather than the previous fixed
    // 500px (which split awkwardly across the 280-px tiles).
    const distance = Math.max(el.clientWidth * 0.9, 280);
    el.scrollBy({
      left: direction === 'left' ? -distance : distance,
      behavior: 'smooth'
    });
  };

  // One comment for both arrows, because the rules interlock.
  //   • HIDDEN only when the row cannot scroll AT ALL. That is the case worth
  //     fixing — a virtual cursor otherwise finds two painted-but-inert arrows
  //     per row, ten or more on a tab whose rows never scroll.
  //   • NEVER hidden per-direction: `canScrollRight` flips as the row reaches
  //     its end, so that would hide the very arrow the player just pressed and
  //     still has focus on. ARIA forbids focus inside `aria-hidden`, and NVDA
  //     and JAWS go silent when it happens.
  //   • A spent direction is `aria-disabled` — true, and still addressable.
  //   • A FOCUSED arrow is neither hidden NOR invisible, however it was spent:
  //     pressing "Scroll right" to the end used to take the focus ring with it
  //     and leave a sighted keyboard user no visible caret (WCAG 2.4.7). Focus
  //     is tracked per arrow so only that one is exempted, which keeps the
  //     noise removal intact for the other. "Focused" means KEYBOARD focus —
  //     see focusArrow.
  //   • LIVE — painted and clickable, as one — when the direction has travel
  //     and the row is revealed (hover or focus within), OR this arrow holds
  //     focus. A visible chevron that did not take the click let a mouse click
  //     fall through to the tile beneath it and open a table nobody asked for
  //     (JRam, 2026-09-14); clicking a spent arrow is harmless — scroll() at
  //     the end of the row is a no-op, and it carries aria-disabled.
  const arrowLive = (side: Side, canScroll: boolean) =>
    (canScroll && arrowsRevealed) || focusedArrow === side;
  const arrowProps = (side: Side, canScroll: boolean): ScrollArrowProps => ({
    side,
    live: arrowLive(side, canScroll),
    canScroll,
    hiddenFromAT: !canScrollAtAll && focusedArrow !== side,
    onPointerDown: () => {
      pointerFocusPending.current = true;
    },
    onFocus: () => focusArrow(side),
    onBlur: () => setFocusedArrow((cur) => (cur === side ? null : cur)),
    onClick: () => scroll(side)
  });

  return (
    <div
      ref={wrapperRef}
      className={styles.wrapper}
      onMouseEnter={supportsHover ? () => setIsHovered(true) : undefined}
      onMouseLeave={supportsHover ? () => setIsHovered(false) : undefined}
      onFocus={() => setIsFocusWithin(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setIsFocusWithin(false);
      }}>
      <div ref={scrollRef} className={className}>
        {children}
      </div>
      <ScrollArrow {...arrowProps('left', canScrollLeft)} />
      <ScrollArrow {...arrowProps('right', canScrollRight)} />
    </div>
  );
};

export default HorizontalScrollRow;
